import { useEffect, useState } from "react";
import { ChevronDown, Headphones, Languages, Megaphone, Mic, Sparkles } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { getDialCapabilities, NO_DIAL_CAPS } from "../../lib/dialCaps";
import { Pop, useClosePops } from "../shell/Pop";
import { AudioPicker } from "./audio/AudioPicker";
import { MobilePick } from "./dial/MobilePick";
import {
  announceBlocker,
  announceOffSilencesPilot,
  effectiveDialFlags,
  initialDialFlags,
  transcribeBlocker,
  type DialCapabilities,
  type DialFlags,
} from "./dialFlags";

export interface DialFlagsState {
  /** What the operator has clicked. Null until the settings arrive. */
  wanted: DialFlags | null;
  setWanted: (next: DialFlags) => void;
  /** What the account makes possible at all. */
  caps: DialCapabilities;
  /**
   * The operator saved or cleared their own mobile, so one field of `caps` is
   * out of date.
   *
   * Only that field is replaced rather than the whole object being re-fetched:
   * everything else in `caps` describes the account, and a second request would
   * be a chance for the strip to change under a half-made choice.
   */
  setMobileNumber: (mobileNumber: string) => void;
  /** What will really be sent with the call, or null while the settings load. */
  effective: DialFlags | null;
}

/**
 * What the next call will be placed with.
 *
 * The account settings seed it and stay the default; from there the two pills
 * belong to the one call about to be made. Owned by the dial pad rather than by
 * the footer, because the pad is what places the call - a footer holding the
 * answer would have to shout it upwards through a callback anyway.
 */
export function useDialFlags(): DialFlagsState {
  const [wanted, setWanted] = useState<DialFlags | null>(null);
  const [caps, setCaps] = useState<DialCapabilities>(NO_DIAL_CAPS);

  useEffect(() => {
    let alive = true;
    // One small endpoint rather than the whole settings object, and that is a
    // permission decision rather than a payload one: `/calls/settings` is the
    // desk's configuration and an operator holding only `use:crm.calls` is not
    // allowed to read it. The four booleans below are all this strip ever
    // wanted, and the server derives them from the same status the settings
    // page renders - see `crm-calls/lib/dial-caps.ts`.
    getDialCapabilities()
      .then((next) => {
        if (!alive) return;
        setCaps(next);
        setWanted(initialDialFlags(next));
      })
      .catch(() => {
        // A dialer that can place calls but cannot read the capabilities still
        // dials. The strip draws nothing and the call goes out on the account
        // default, which is exactly what an omitted flag means to the server.
      });
    return () => {
      alive = false;
    };
  }, []);

  return {
    wanted,
    setWanted,
    caps,
    setMobileNumber: (mobileNumber: string) => setCaps((prev) => ({ ...prev, mobileNumber })),
    effective: wanted ? effectiveDialFlags(wanted, caps) : null,
  };
}

/**
 * What will happen to the call that is about to be placed, and the two keys
 * that already work.
 *
 * The three pills are switches for THIS call. The account-wide setting under
 * Settings -> Telephony is still where the default comes from, and clicking here
 * never writes it back: the strip answers "this call", not "from now on".
 *
 * The transcript pill goes dead in two situations and says which one in its
 * tooltip: no Gemini key at all, and no recording to read - the after-call pass
 * works off the stored audio, so a call that keeps none has nothing to give it.
 *
 * The announcement pill only ever turns the notice OFF for one call, and goes
 * dead when there is no announcement configured to begin with. It is deliberately
 * a per-call switch and not a quiet default: what the person picking up is told
 * is not the kind of thing that should change without somebody choosing it.
 *
 * The audio pill is the odd one out and deliberately so: it is not a switch for
 * this call but a door to the devices the call will use, and it is here because
 * the dial pad is the one screen an operator reliably opens BEFORE a call goes
 * wrong. The same panel is reachable mid-call from the controls row - the
 * difference is that by then somebody is already saying they cannot hear you.
 *
 * The key hints cost nothing and were already true: Enter dials from the number
 * field, Escape closes the window. Nobody knew, because nothing said so.
 */
export function DialerFoot({ flags }: { flags: DialFlagsState }) {
  const t = useT();
  const closePops = useClosePops();
  const { wanted, setWanted, caps, setMobileNumber, effective } = flags;
  const blocker = wanted ? transcribeBlocker(wanted, caps) : null;
  const noticeBlocked = announceBlocker(caps) !== null;

  return (
    <div className="crm-dfoot">
      {wanted && effective ? (
        <>
          <button
            type="button"
            role="switch"
            aria-checked={effective.record}
            className={cn("crm-dtog", effective.record && "is-on")}
            title={t(effective.record ? "calls.dial.recordOnHint" : "calls.dial.recordOffHint")}
            onClick={() => setWanted({ ...wanted, record: !wanted.record })}
          >
            <i />
            <Mic className="size-3.5" />
            {t("calls.dial.record")}
          </button>
          <button
            type="button"
            role="switch"
            aria-checked={effective.transcribe}
            disabled={blocker !== null}
            className={cn("crm-dtog", effective.transcribe && "is-on")}
            title={t(
              blocker === "no_key"
                ? "calls.dial.transcribeNoKeyHint"
                : blocker === "no_recording"
                  ? "calls.dial.transcribeNoRecordHint"
                  : effective.transcribe
                    ? "calls.dial.transcribeOnHint"
                    : "calls.dial.transcribeOffHint",
            )}
            onClick={() => setWanted({ ...wanted, transcribe: !wanted.transcribe })}
          >
            <i />
            <Sparkles className="size-3.5" />
            {t("calls.dial.transcribe")}
          </button>
          <button
            type="button"
            role="switch"
            aria-checked={effective.announce}
            disabled={noticeBlocked}
            className={cn("crm-dtog", effective.announce && "is-on")}
            title={t(
              noticeBlocked
                ? "calls.dial.announceNoNoticeHint"
                : effective.announce
                  ? "calls.dial.announceOnHint"
                  : // Not a footnote: the AI panel going quiet is the second
                    // half of this click, and the operator finds out either
                    // here or by staring at an empty panel mid-call.
                    announceOffSilencesPilot(effective, caps)
                    ? "calls.dial.announceOffPilotHint"
                    : "calls.dial.announceOffHint",
            )}
            onClick={() => setWanted({ ...wanted, announce: !wanted.announce })}
          >
            <i />
            <Megaphone className="size-3.5" />
            {t("calls.dial.announce")}
          </button>

          {/* Only where there is a choice to make. An account with no
              translation gets no control at all rather than a picker holding
              one item - and the list is already filtered by the server to the
              languages that would really change what THIS caller hears, so a
              language written for a wording this call does not use never
              appears here to be picked and quietly ignored.

              Disabled by the same `noticeBlocked` as the pill it follows: a
              language choice on a call that announces nothing is a control with
              no effect. */}
          {caps.noticeLocales.length > 0 ? (
            <Pop
              trigger={(open) => (
                <button
                  type="button"
                  className={cn("crm-dtog", (open || effective.announceLocale) && "is-on")}
                  aria-haspopup="listbox"
                  aria-expanded={open}
                  disabled={noticeBlocked || !effective.announce}
                  title={t("calls.dial.announceLangHint")}
                >
                  <Languages className="size-3.5" />
                  {effective.announceLocale
                    ? (caps.noticeLocales.find((e) => e.locale === effective.announceLocale)?.label ??
                      t("calls.dial.announceLangBase"))
                    : t("calls.dial.announceLangBase")}
                  <ChevronDown className={cn("size-3.5 transition-transform", open && "rotate-180")} />
                </button>
              )}
            >
              {/* The account's own wording is an option rather than the
                  absence of one, so an operator who picked Russian by mistake
                  has somewhere to go back to. */}
              <button
                type="button"
                className={cn("crm-mi", wanted.announceLocale === null && "is-on")}
                onClick={() => {
                  setWanted({ ...wanted, announceLocale: null });
                  closePops();
                }}
              >
                {t("calls.dial.announceLangBase")}
              </button>
              {caps.noticeLocales.map((entry) => (
                <button
                  key={entry.locale}
                  type="button"
                  className={cn("crm-mi", wanted.announceLocale === entry.locale && "is-on")}
                  onClick={() => {
                    setWanted({ ...wanted, announceLocale: entry.locale });
                    closePops();
                  }}
                >
                  {entry.label}
                </button>
              ))}
            </Pop>
          ) : null}
          {/* Last of the strip and after the language, because it is the only
              control here that is not about the CALL: the three before it
              decide what happens to the conversation, this decides where the
              operator is sitting. Drawn only where it could work at all - see
              `MobilePick` on why a dead one would be worse than none. */}
          {caps.conference ? (
            <MobilePick
              wanted={wanted}
              effective={effective}
              caps={caps}
              setWanted={setWanted}
              onNumberSaved={setMobileNumber}
            />
          ) : null}
        </>
      ) : null}

      {/* Not behind the settings load like the three above it: the devices this
          browser can reach have nothing to do with what the account allows, and
          an operator whose settings request failed is exactly the one who may
          need to fix their headset. */}
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-dtog", open && "is-on")}
            aria-haspopup="listbox"
            aria-expanded={open}
            title={t("calls.audio.hint")}
          >
            <Headphones className="size-3.5" />
            {t("calls.audio.label")}
            <ChevronDown className={cn("size-3.5 transition-transform", open && "rotate-180")} />
          </button>
        )}
      >
        {/* Null: the pad is never on screen while a call is up, so there is no
            live routing to change - the choice is stored and applied by
            `CallProvider` at the moment the call is placed. */}
        <AudioPicker liveAudio={null} />
      </Pop>

      <span className="crm-dkeys">
        <span>
          <b>&crarr;</b>
          {t("calls.dial.keyCall")}
        </span>
        <span>
          <b>esc</b>
          {t("calls.dial.keyClose")}
        </span>
      </span>
    </div>
  );
}
